import { applyCountryImages, DEFAULT_COUNTRY_IMAGE } from "./countryImages";
import { COUNTRY_FIGURES } from "@/lib/media";
import { cleanCopyDeep } from "@/lib/formatCopy";

// Total number of colonies / countries (1834–1917 Girmitiya system)
export const TOTAL_COUNTRIES = 16;

const rawCountriesData = [
    {
        slug: "mauritius",
        name: "Mauritius",
        nameHindi: "मॉरीशस",
        countryIndex: 1,
        region: "Indian Ocean",
        mapConfig: { coordinates: [57.5522, -20.3484], scale: 3000 },
        subtitle: "From 1834, the 'Great Experiment' began here, the world's first and largest Girmitiya destination.",
        arrivalDate: "November 2, 1834",
        periodStart: "1834",
        periodEnd: "1900",
        totalTransported: "453,063",
        currentPopulation: "Approx. 850,000 (68% of population)",
        originRegions: ["Bihar", "Uttar Pradesh", "Tamil Nadu"],
        description:
            "Mauritius was the first colony to receive Indian indentured labourers under the Girmitiya system (1834–1917). Nearly 453,063 workers arrived between 1834 and 1900, fundamentally shaping the island's demographics and culture. Aapravasi Ghat in Port Louis is a UNESCO World Heritage Site marking this legacy.",
        timeline: [
            { year: "1834", desc: "Ship Atlas arrived, the world's first organised indentured voyage. The 'Great Experiment' began in Port Louis." },
            { year: "1838", desc: "Over 25,000 Indians sent within four years. More than 17% died on the journey from cholera, dysentery, and overcrowding." },
            { year: "1849", desc: "Aapravasi Ghat (Immigration Depot) built in Port Louis, later a UNESCO World Heritage Site (2006)." },
            { year: "1872", desc: "Investigation commission exposed systematic exploitation, beatings, low wages, and denial of medical care." },
            { year: "1900", desc: "By 1900, a total of 453,063 Girmitiya labourers had arrived, the largest volume under indenture." },
            { year: "1968", desc: "Independence achieved. Sir Seewoosagur Ramgoolam, a descendant of Girmitiyas, became the first Prime Minister." },
        ],
        figures: [
            {
                name: "Sir Seewoosagur Ramgoolam",
                role: "First Prime Minister",
                initials: "SR",
                desc: "First Prime Minister of Mauritius (1968). Grandson of Girmitiya labourers. Known as the 'Father of the Nation'.",
                image: COUNTRY_FIGURES.mauritius.src,
            },
        ],
        gallery: [],
        reports: [],
    },
    {
        slug: "guyana",
        name: "British Guiana (Guyana)",
        nameHindi: "ब्रिटिश गयाना",
        countryIndex: 2,
        region: "South America",
        mapConfig: { coordinates: [-58.9302, 4.8604], scale: 1200 },
        subtitle: "From 1838, the Caribbean's second-largest Girmitiya colony, 238,909 labourers on sugar estates.",
        arrivalDate: "May 5, 1838",
        periodStart: "1838",
        periodEnd: "1916",
        totalTransported: "238,909",
        currentPopulation: "Approx. 310,000 (39% of population)",
        originRegions: ["Bhojpur", "Awadh", "Chota Nagpur"],
        description:
            "British Guiana (now Guyana) received 238,909 Indian indentured labourers between 1838 and 1916. The Gladstone Experiment of 1838 brought the first wave, transforming coastal sugar estates and creating one of the largest Indo-Caribbean communities.",
        timeline: [
            { year: "1838", desc: "5 May, Whitby and Hesperus arrived in Berbice. The first Indians landed in the Caribbean." },
            { year: "1838", desc: "Men earned 25 cents per day, women 16 cents. Leaving the estate without permission was a criminal offence." },
            { year: "1900", desc: "Indians became the largest ethnic group. They transformed Guyana into a major rice-exporting nation." },
            { year: "1916", desc: "Indenture system abolished. A total of 238,909 Girmitiyas came to this colony." },
        ],
        figures: [
            {
                name: "Cheddi Jagan",
                role: "First Freely Elected President",
                initials: "CJ",
                desc: "Guyana's first freely elected President (1992). A descendant of Girmitiyas from Port Mourant.",
                image: COUNTRY_FIGURES.guyana.src,
            },
        ],
        gallery: [],
        reports: [{ title: "The Des Voeux Commission Report", year: "1870", author: "George W. Des Voeux" }],
    },
    {
        slug: "trinidad-tobago",
        name: "Trinidad",
        nameHindi: "त्रिनिदाद",
        countryIndex: 3,
        region: "Caribbean",
        mapConfig: { coordinates: [-61.2225, 10.6918], scale: 3000 },
        subtitle: "Ship Fatel Razack, 30 May 1845, birthplace of Indian Arrival Day, now a national public holiday.",
        arrivalDate: "May 30, 1845",
        periodStart: "1845",
        periodEnd: "1916",
        totalTransported: "143,939",
        currentPopulation: "Approx. 480,000 (35% of population)",
        originRegions: ["Uttar Pradesh", "Bihar"],
        description:
            "Trinidad received 143,939 Indian indentured labourers between 1845 and 1916. The Fatel Razack brought the first workers in 1845. Indo-Trinidadian culture deeply influences the island's cuisine, music, and politics today.",
        timeline: [
            { year: "1845", desc: "30 May, Fatel Razack arrived in Port of Spain with 217 Indians aboard." },
            { year: "1886", desc: "Workers sang verses from the Ramayana in the fields each evening, culture endured." },
            { year: "1916", desc: "Indenture abolished. A total of 143,939 Girmitiyas reached Trinidad." },
            { year: "1995", desc: "Basdeo Panday became the first Indo-Trinidadian Prime Minister." },
        ],
        figures: [
            {
                name: "V.S. Naipaul",
                role: "Nobel Laureate",
                initials: "VN",
                desc: "Nobel Prize in Literature (2001). Of Indo-Trinidadian descent.",
                image: COUNTRY_FIGURES["trinidad-tobago"].src,
            },
        ],
        gallery: [],
        reports: [],
    },
    {
        slug: "jamaica",
        name: "Jamaica",
        nameHindi: "जमैका",
        countryIndex: 4,
        region: "Caribbean",
        mapConfig: { coordinates: [-77.2975, 18.1096], scale: 2000 },
        subtitle: "From 1845, 36,412 Girmitiya labourers rebuilt the sugar industry after emancipation.",
        arrivalDate: "1845",
        periodStart: "1845",
        periodEnd: "1913",
        totalTransported: "36,412",
        currentPopulation: "Approx. 90,000",
        originRegions: ["Northern India", "Awadh"],
        description:
            "Jamaica imported 36,412 Indian indentured labourers between 1845 and 1913 to sustain its sugar industry after emancipation. Though smaller in scale than other Caribbean colonies, the Indo-Jamaican community preserved distinct cultural traditions.",
        timeline: [
            { year: "1845", desc: "Ship Blundell Hunter arrived at Old Harbour, the first Indian indentured labourers." },
            { year: "1913", desc: "Indenture system ended in Jamaica. A total of 36,412 labourers had arrived." },
        ],
        figures: [
            {
                name: "Rajiv Maragh",
                role: "Champion Jockey",
                initials: "RM",
                desc: "Prominent Jamaican jockey of Indian heritage, representing the sporting achievements of the Indo-Jamaican community.",
                image: COUNTRY_FIGURES.jamaica.src,
            },
        ],
        gallery: [],
        reports: [],
    },
    {
        slug: "grenada",
        name: "Grenada",
        nameHindi: "ग्रीनाडा",
        countryIndex: 5,
        region: "Caribbean",
        mapConfig: { coordinates: [-61.6790, 12.1165], scale: 4000 },
        subtitle: "The Spice Island, from 1856, 3,200 Girmitiya labourers worked sugar and nutmeg estates.",
        arrivalDate: "1856",
        periodStart: "1856",
        periodEnd: "1885",
        totalTransported: "3,200",
        currentPopulation: "Approx. 12,000",
        originRegions: ["Uttar Pradesh", "Bihar"],
        description:
            "Grenada received approximately 3,200 Indian indentured labourers between 1856 and 1885. Known as the 'Spice Island', Indo-Grenadians became part of the nation's multicultural fabric.",
        timeline: [
            { year: "1856", desc: "First Indian indentured labourers arrived in Grenada, deployed on sugar estates." },
            { year: "1885", desc: "Indenture ended. Approximately 3,200 Girmitiyas had arrived." },
        ],
        figures: [],
        gallery: [],
        reports: [],
    },
    {
        slug: "saint-lucia",
        name: "Saint Lucia",
        nameHindi: "सेंट लूसिया",
        countryIndex: 6,
        region: "Caribbean",
        mainLocation: "Castries",
        mapConfig: { coordinates: [-60.9789, 13.9094], scale: 4000 },
        subtitle: "1858–1895: 4,350 indentured labourers on sugar estates, fragments of Indian heritage still survive.",
        arrivalDate: "1858",
        periodStart: "1858",
        periodEnd: "1895",
        totalTransported: "4,350",
        currentPopulation: "Approx. 3,000",
        originRegions: ["Uttar Pradesh", "Bihar"],
        description:
            "Saint Lucia received Indian indentured labourers after slavery ended. Though the Indian population remained small, traces of Indian heritage still survive through food, surnames, oral traditions, and Bhojpuri heritage.",
        timeline: [
            { year: "1858", desc: "Arrival of the first Indian indentured labourers." },
            { year: "1860s", desc: "Indians mainly worked in sugar estates under harsh conditions." },
            { year: "1880s", desc: "Many labourers completed contracts and settled permanently." },
            { year: "1895", desc: "Indenture recruitment declined and migration gradually stopped." },
            { year: "Today", desc: "Descendants preserve fragments of Indian identity." },
        ],
        figures: [
            {
                name: "Sir John Compton",
                role: "Prime Minister of Saint Lucia",
                initials: "JC",
                desc: "Long-serving Prime Minister who helped shape modern Saint Lucia. Saint Lucia's Indian heritage survives through families who preserved Bhojpuri traditions and Hindu customs.",
                image: COUNTRY_FIGURES["saint-lucia"].src,
            },
        ],
        occupations: ["Sugar plantation labour", "Agricultural estate work"],
        culturalLegacy: ["Food", "Surnames", "Oral traditions", "Hindu customs", "Bhojpuri heritage"],
        gallery: [],
        reports: [],
    },
    {
        slug: "saint-kitts",
        name: "Saint Kitts",
        nameHindi: "सेंट किट्स",
        countryIndex: 7,
        region: "Caribbean",
        mainLocation: "Basseterre",
        mapConfig: { coordinates: [-62.7830, 17.3578], scale: 5000 },
        subtitle: "1860–1861: only 337 Girmitiya, one of the smallest indenture destinations in the Caribbean.",
        arrivalDate: "1860",
        periodStart: "1860",
        periodEnd: "1861",
        totalTransported: "337",
        currentPopulation: "Small diaspora",
        originRegions: ["Uttar Pradesh", "Bihar"],
        description:
            "One of the smallest destinations of Indian indenture where workers were brought mainly for sugar plantation labour during British rule. Workers faced harsh conditions and cultural isolation; Indian ancestry survives in scattered family records.",
        timeline: [
            { year: "1860", desc: "First Indian labourers arrived." },
            { year: "1861", desc: "Indenture system remained limited because of the island's small plantation economy." },
            { year: "1880s", desc: "Workers faced harsh labour conditions and cultural isolation." },
            { year: "After 1861", desc: "Recruitment gradually ended and many moved to nearby islands." },
            { year: "Today", desc: "Indian ancestry survives in scattered family records." },
        ],
        figures: [
            { name: "Early Plantation Workers of Basseterre", role: "Community Pioneers", initials: "BP", desc: "Remembered as pioneers of the Indian community on Saint Kitts.", image: "" },
        ],
        occupations: ["Sugar plantation labour"],
        challenges: ["Harsh working conditions", "Small labour economy", "Cultural isolation"],
        culturalLegacy: ["Family histories", "Archival records", "Ancestry traces"],
        gallery: [],
        reports: [],
    },
    {
        slug: "saint-vincent",
        name: "Saint Vincent",
        nameHindi: "सेंट विंसेंट",
        countryIndex: 8,
        region: "Caribbean",
        mainLocation: "Kingstown",
        mapConfig: { coordinates: [-61.2872, 13.2528], scale: 4000 },
        subtitle: "1860–1880: 2,472 Girmitiya revived declining plantations after emancipation.",
        arrivalDate: "1860",
        periodStart: "1860",
        periodEnd: "1880",
        totalTransported: "2,472",
        currentPopulation: "Approx. 5,000",
        originRegions: ["Uttar Pradesh", "Bihar"],
        description:
            "Indians were brought mainly to revive the declining plantation economy after emancipation. Some integrated into Creole society while others preserved Hindu and Muslim traditions.",
        timeline: [
            { year: "1860", desc: "First Indian indentured workers arrived." },
            { year: "1870s", desc: "Indians became important in sugar and agricultural production." },
            { year: "1880", desc: "Recruitment under indenture ended." },
            { year: "Late 1800s", desc: "Some Indians integrated into Creole society while others preserved Hindu and Muslim traditions." },
            { year: "Today", desc: "Indo-Vincentian heritage survives through festivals and memory." },
        ],
        figures: [
            {
                name: "Dr. Arnold Thomas",
                role: "Community Leader",
                initials: "AT",
                desc: "Early Indo-Vincentian leader preserving Indian customs, family traditions, and community memory on the island.",
                image: COUNTRY_FIGURES["saint-vincent"].src,
            },
        ],
        occupations: ["Sugar production", "Agricultural labour"],
        culturalLegacy: ["Festivals", "Hindu traditions", "Muslim traditions", "Community memory"],
        gallery: [],
        reports: [],
    },
    {
        slug: "nevis",
        name: "Nevis",
        nameHindi: "नेविस",
        countryIndex: 9,
        region: "Caribbean",
        mainLocation: "Charlestown",
        mapConfig: { coordinates: [-62.5833, 17.1500], scale: 6000 },
        subtitle: "1874–1875: approx. 300 Girmitiya, one of the smallest indenture destinations.",
        arrivalDate: "1874",
        periodStart: "1874",
        periodEnd: "1875",
        totalTransported: "300",
        currentPopulation: "Small diaspora",
        originRegions: ["Uttar Pradesh", "Bihar"],
        description:
            "A very small number of Indian labourers were transported here during the late indenture period under British plantation policies. Indian presence survives mainly through records and ancestry traces.",
        timeline: [
            { year: "1874", desc: "Recruitment of Indian labourers began." },
            { year: "1875", desc: "Small Indian groups worked mainly in sugar estates." },
            { year: "Late 1800s", desc: "Most labourers migrated elsewhere or assimilated locally." },
            { year: "Today", desc: "Indian presence survives mainly through records and ancestry traces." },
        ],
        figures: [
            { name: "Unnamed Early Labour Families", role: "Symbolic Representatives", initials: "EL", desc: "Symbolic representatives of the small Indian community once present on Nevis.", image: "" },
        ],
        occupations: ["Sugar estate labour"],
        culturalLegacy: ["Archival records", "Family ancestry traces"],
        gallery: [],
        reports: [],
    },
    {
        slug: "south-africa",
        name: "Natal (South Africa)",
        nameHindi: "नटाल",
        countryIndex: 10,
        region: "Southern Africa",
        mapConfig: { coordinates: [30.5595, -29.8587], scale: 2000 },
        subtitle: "Ship Truro, 16 November 1860, 152,184 Girmitiya where Gandhi pioneered Satyagraha.",
        arrivalDate: "November 16, 1860",
        periodStart: "1860",
        periodEnd: "1911",
        totalTransported: "152,184",
        currentPopulation: "Approx. 1,300,000",
        originRegions: ["Madras Presidency", "Bihar", "Awadh"],
        description:
            "The Colony of Natal imported 152,184 Indian indentured labourers for its sugar plantations between 1860 and 1911. This became the crucible where Mahatma Gandhi developed Satyagraha while fighting for Girmitiya rights.",
        timeline: [
            { year: "1860", desc: "16 Nov, Truro arrived in Durban with 342 Indians. The first Girmitiya labourers in Natal." },
            { year: "1893", desc: "Gandhi arrived in Natal. He was removed from a train at Pietermaritzburg station." },
            { year: "1911", desc: "Indenture ended in Natal. A total of 152,184 labourers had arrived." },
            { year: "1913", desc: "Gandhi led the 'Great March', over 2,000 Indian labourers joined the protest." },
        ],
        figures: [
            {
                name: "Mohandas Gandhi",
                role: "Satyagraha Pioneer",
                initials: "MG",
                desc: "Laid the foundations of Satyagraha in Natal while fighting for Girmitiya rights.",
                image: COUNTRY_FIGURES["south-africa"].src,
            },
        ],
        gallery: [],
        reports: [],
    },
    {
        slug: "fiji",
        name: "Fiji",
        nameHindi: "फिजी",
        countryIndex: 11,
        region: "Pacific Ocean",
        mapConfig: { coordinates: [178.0650, -17.7134], scale: 3000 },
        subtitle: "Ship Leonidas, 14 May 1879, 60,965 Girmitiya, the Pacific's largest indenture destination.",
        arrivalDate: "May 14, 1879",
        periodStart: "1879",
        periodEnd: "1916",
        totalTransported: "60,965",
        currentPopulation: "Approx. 313,000 (37% of population)",
        originRegions: ["Uttar Pradesh", "Bihar", "Madras Presidency"],
        description:
            "Between 1879 and 1916, 60,965 Indians were brought to Fiji under the indenture system. They called themselves 'Girmitiyas'. Today, Indo-Fijians form a vital part of the nation's cultural and economic fabric.",
        timeline: [
            { year: "1879", desc: "Leonidas arrived at Levuka, the first organised indentured voyage to the Pacific." },
            { year: "1913", desc: "Totaram Sanadhya exposed indenture abuses, inspiring Gandhi's activism." },
            { year: "1916", desc: "Indenture abolished. A total of 60,965 Girmitiyas reached Fiji." },
            { year: "Today", desc: "Australia's National Library has digitised over 60,500 immigration passes." },
        ],
        figures: [
            {
                name: "Totaram Sanadhya",
                role: "Author & Activist",
                initials: "TS",
                desc: "Author of My Twenty-One Years in the Fiji Islands (1914), exposing plantation abuses.",
                image: COUNTRY_FIGURES.fiji.src,
            },
        ],
        gallery: [],
        reports: [
            { title: "Report on the Conditions of Indian Immigrants in Fiji", year: "1914", author: "C.F. Andrews & W.W. Pearson" },
        ],
    },
    {
        slug: "malaysia",
        name: "Malaya (Malaysia)",
        nameHindi: "मलाया",
        countryIndex: 12,
        region: "Southeast Asia",
        mainLocation: "Kuala Lumpur / Penang",
        mapConfig: { coordinates: [101.9758, 4.2105], scale: 1500 },
        subtitle: "1844–1910: approx. 250,000 Indians built Malaya's rubber plantations, railways, and infrastructure.",
        arrivalDate: "1844",
        periodStart: "1844",
        periodEnd: "1910",
        totalTransported: "250,000",
        currentPopulation: "Approx. 2,000,000",
        originRegions: ["Tamil Nadu", "Andhra Pradesh", "Kerala"],
        description:
            "Malaysia became one of the largest destinations for Indian indentured labourers during British colonial rule, especially for rubber plantations, railway construction, and infrastructure projects. Tamil workers became the backbone of the plantation economy, and Malaysian Indians remain a major cultural and economic community today.",
        timeline: [
            { year: "1844", desc: "Indians first arrived in large numbers under British labour schemes." },
            { year: "Late 1800s", desc: "Tamil workers became the backbone of rubber plantation labour." },
            { year: "1900s", desc: "Indians contributed heavily to railways, roads, and infrastructure." },
            { year: "1910", desc: "Indenture recruitment officially reduced as labour systems changed." },
            { year: "Today", desc: "Malaysian Indians remain a major cultural and economic community." },
        ],
        figures: [
            {
                name: "Janaky Athi Nahappan",
                role: "Freedom Fighter & Social Reformer",
                initials: "JN",
                desc: "Malaysian independence activist who helped build the plantation economy and preserved Indian traditions abroad.",
                image: COUNTRY_FIGURES.malaysia.src,
            },
        ],
        contributions: ["Rubber plantation workforce", "Railway building", "Road construction", "Economic development"],
        occupations: ["Rubber plantations", "Railway construction", "Infrastructure projects"],
        culturalLegacy: ["Tamil communities", "Hindu temples", "Food traditions", "Festivals", "Business communities"],
        gallery: [],
        reports: [],
    },
    {
        slug: "east-africa",
        name: "East Africa",
        nameHindi: "ईस्ट अफ्रीका",
        countryIndex: 13,
        region: "East Africa",
        mapConfig: { coordinates: [36.8219, -1.2921], scale: 1200 },
        subtitle: "From 1896, 39,000 Girmitiya built the Uganda Railway from Mombasa to Lake Victoria.",
        arrivalDate: "1896",
        periodStart: "1896",
        periodEnd: "1921",
        totalTransported: "39,000",
        currentPopulation: "Approx. 100,000+ across region",
        originRegions: ["Punjab", "Gujarat", "Tamil Nadu"],
        description:
            "East Africa (Kenya, Uganda, Tanganyika) received approximately 39,000 Indian indentured labourers between 1896 and 1921, primarily for construction of the Uganda Railway and plantation work. Their sacrifices laid the foundation for modern East African infrastructure.",
        timeline: [
            { year: "1896", desc: "Indian labourers arrived to build the Uganda Railway, from Mombasa to Lake Victoria." },
            { year: "1901", desc: "Railway completed. Thousands of workers lost their lives, known as the 'Lunatic Line'." },
            { year: "1921", desc: "Indenture ended in East Africa. A total of 39,000 labourers contributed." },
        ],
        figures: [
            {
                name: "Makhan Singh",
                role: "Trade Unionist & Activist",
                initials: "MS",
                desc: "Kenyan trade union leader who fought for workers' rights during the colonial era and represented the Indian labour community in East Africa.",
                image: COUNTRY_FIGURES["east-africa"].src,
            },
        ],
        gallery: [],
        reports: [],
    },
    {
        slug: "seychelles",
        name: "Seychelles",
        nameHindi: "सेशेल्स",
        countryIndex: 14,
        region: "Indian Ocean",
        mainLocation: "Victoria",
        mapConfig: { coordinates: [55.4540, -4.6796], scale: 5000 },
        subtitle: "1904–1916: 6,315 Indians as labourers, traders, and skilled workers under British rule.",
        arrivalDate: "1904",
        periodStart: "1904",
        periodEnd: "1916",
        totalTransported: "6,315",
        currentPopulation: "Approx. 5,000",
        originRegions: ["Gujarat", "Tamil Nadu", "Bihar"],
        description:
            "Indians arrived mainly as labourers, traders, and skilled workers under British colonial administration. Indian-origin communities became economically influential, with heritage visible in business, religion, and festivals.",
        timeline: [
            { year: "1904", desc: "Indian recruitment and migration increased." },
            { year: "1900s", desc: "Indians contributed to trade, port work, and plantation activities." },
            { year: "1916", desc: "Indenture-linked migration slowed significantly." },
            { year: "Mid-20th Century", desc: "Indian-origin communities became economically influential." },
            { year: "Today", desc: "Indian heritage remains visible in business, religion, and festivals." },
        ],
        figures: [
            {
                name: "Wavel Ramkalawan",
                role: "President of Seychelles",
                initials: "WR",
                desc: "President of Seychelles (2020). Of Indian descent, representing the commercial and cultural legacy of the Indian community.",
                image: COUNTRY_FIGURES.seychelles.src,
            },
        ],
        occupations: ["Trade", "Port work", "Plantation activities", "Skilled labour"],
        culturalLegacy: ["Commerce", "Temples", "Religion", "Festivals", "Business communities"],
        gallery: [],
        reports: [],
    },
    {
        slug: "reunion",
        name: "Réunion (France)",
        nameHindi: "रियूनियन",
        countryIndex: 15,
        region: "Indian Ocean",
        mapConfig: { coordinates: [55.5364, -21.1151], scale: 4000 },
        subtitle: "From 1861, 26,507 Girmitiya, birthplace of the 'Malbar' community in a French colony.",
        arrivalDate: "1861",
        periodStart: "1861",
        periodEnd: "1883",
        totalTransported: "26,507",
        currentPopulation: "Approx. 250,000",
        originRegions: ["Tamil Nadu", "Pondicherry"],
        description:
            "The French island of Réunion received 26,507 Indian indentured labourers between 1861 and 1883. Known locally as 'Malbars', they preserved Tamil heritage despite French assimilation policies.",
        timeline: [
            { year: "1861", desc: "France began Indian indentured recruitment in Réunion." },
            { year: "1870", desc: "Kalimata temple built in Saint-Denis, the first Girmitiya temple in French territory." },
            { year: "1883", desc: "Indenture ended. A total of 26,507 labourers had arrived." },
        ],
        figures: [
            { name: "Kavi Teelockhy", role: "Poet & Journalist", initials: "KT", desc: "Contributed to education and awareness within the Indian indentured community.", image: "" },
        ],
        gallery: [],
        reports: [],
    },
    {
        slug: "suriname",
        name: "Suriname",
        nameHindi: "सूरीनाम",
        countryIndex: 16,
        region: "South America",
        mapConfig: { coordinates: [-56.0278, 3.9193], scale: 1200 },
        subtitle: "Ship Lalla Rookh, 5 June 1873, 34,304 Hindustani; Sarnami Hindustani is still spoken today.",
        arrivalDate: "June 5, 1873",
        periodStart: "1873",
        periodEnd: "1916",
        totalTransported: "34,304",
        currentPopulation: "Approx. 160,000 (27% of population)",
        originRegions: ["Bhojpur", "Awadh"],
        description:
            "Suriname received 34,304 Indian indentured labourers between 1873 and 1916. Known locally as Hindustanis, they maintained Sarnami Hindustani language and vibrant Hindu-Muslim traditions.",
        timeline: [
            { year: "1873", desc: "5 June, Lalla Rookh arrived in Paramaribo with 336 Indians aboard." },
            { year: "1916", desc: "Indenture abolished. A total of 34,304 Girmitiyas reached Suriname." },
            { year: "2020", desc: "Chandrikapersad Santokhi became President of Suriname." },
        ],
        figures: [
            {
                name: "Chandrikapersad Santokhi",
                role: "President of Suriname",
                initials: "CS",
                desc: "President of Suriname (2020). Of Indian descent.",
                image: COUNTRY_FIGURES.suriname.src,
            },
        ],
        gallery: [],
        reports: [],
    },
];

function attachFigureAlts(countries) {
    return countries.map((country) => ({
        ...country,
        figures: country.figures?.map((fig) => {
            const media = COUNTRY_FIGURES[country.slug];
            return media ? { ...fig, imageAlt: media.alt } : fig;
        }),
    }));
}

export const countriesData = attachFigureAlts(
    applyCountryImages(cleanCopyDeep(rawCountriesData))
);

export const getCountryData = (slug) => {
    const found = countriesData.find((c) => c.slug === slug);
    if (found) return found;

    return {
        slug,
        name: slug.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" "),
        mapConfig: { coordinates: [0, 0], scale: 200 },
        cardImage: DEFAULT_COUNTRY_IMAGE,
        heroImage: DEFAULT_COUNTRY_IMAGE,
        arrivalDate: "19th Century",
        periodStart: "1834",
        periodEnd: "1917",
        totalTransported: "Records being digitized...",
        currentPopulation: "Significant diaspora",
        originRegions: ["Various Regions in India"],
        description:
            "The Girmitiya history of this colony is being documented by our archives. More historical data, passenger lists, and cultural reports will be published soon.",
        timeline: [{ year: "1800s", desc: "Indian indentured labourers arrived to work on local plantations." }],
        figures: [],
        gallery: [],
        reports: [],
    };
};
